import { Drawer, Collapse, Table, Spin, Tooltip } from 'antd';
import { ExclamationCircleFilled, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import {
    ValidatePlanSection,
    ValidatePlanTable,
    ValidatePlanColumn,
    ValidatePlanRow,
    ValidatePlanSectionMetadata,
    ValidatePlanCellValue,
    isStructuredCellValue,
} from 'types/tpo/validatePlan';
import type { ColumnsType } from 'antd/es/table';
import { useState, useMemo } from 'react';
import styled from '@emotion/styled';

const { Panel } = Collapse;

// Styled Table wrapper for validate plan tables
const StyledValidatePlanTable = styled(Table)`
    background: white;
    border-radius: 8px;

    .ant-table {
        background: transparent;
    }

    .ant-table-thead > tr > th {
        background: #f0f0f0 !important;
        font-weight: 500;
        font-size: 13px;
        color: #4e4c55;
        border-bottom: 1px solid #f5f5f5;
        padding: 10px 12px;
        white-space: nowrap;
    }

    .ant-table-tbody > tr > td {
        font-size: 13px;
        padding: 10px 12px;
        border-bottom: 1px solid #f5f5f5;
        white-space: nowrap;
    }

    .ant-table-tbody > tr:last-child > td {
        border-bottom: none;
    }

    .ant-table-tbody > tr:hover > td {
        background: #fafafa;
    }

    .ant-table-column-sorter {
        color: #bfbfbf;
    }

    .ant-table-column-sorter-up.active,
    .ant-table-column-sorter-down.active {
        color: #44338a;
    }

    .ant-table-body {
        overflow: auto !important;
    }

    .ant-table-body::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    .ant-table-body::-webkit-scrollbar-thumb {
        background: #d9d9d9;
        border-radius: 3px;
    }

    .ant-table-body::-webkit-scrollbar-thumb:hover {
        background: #bfbfbf;
    }

    .ant-table-body::-webkit-scrollbar-track {
        background: #f5f5f5;
        border-radius: 3px;
    }
`;

interface ValidatePlanDrawerProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    sections: ValidatePlanSection[];
    width?: number;
    topOffset?: number;
    loading?: boolean;
    error?: boolean | null;
}

type SortOrder = 'ascend' | 'descend' | null;

interface SortState {
    columnKey: string | null;
    order: SortOrder;
}

// Helper to format observed value (handles arrays)
const formatObservedValue = (value: number | string | number[] | string[] | null): string => {
    if (value === null) return '-';
    if (Array.isArray(value)) {
        return value.join(', ');
    }
    return String(value);
};

// Helper to format expected value for display
const formatExpectedValue = (expectedValue: ValidatePlanCellValue['expectedValue']): string => {
    if (!expectedValue) return '';

    if (expectedValue.type === 'value' && expectedValue.recommendedValue !== null) {
        return `(${expectedValue.recommendedValue})`;
    } else if (expectedValue.type === 'range' && expectedValue.min !== null && expectedValue.max !== null) {
        return `(${expectedValue.min}-${expectedValue.max})`;
    } else if (expectedValue.type === 'enum') {
        if (expectedValue.allowedValues && expectedValue.allowedValues.length > 0) {
            return `(Allowed: ${expectedValue.allowedValues.slice(0, 2).join(', ')}${
                expectedValue.allowedValues.length > 2 ? '...' : ''
            })`;
        }
        if (expectedValue.disallowedValues && expectedValue.disallowedValues.length > 0) {
            return `(Not allowed: ${expectedValue.disallowedValues.join(', ')})`;
        }
    }
    return '';
};

// Helper to get full tooltip text for a cell value
const getTooltipText = (value: string | number | null | ValidatePlanCellValue): string => {
    if (value === null || value === '-') {
        return '-';
    }

    if (isStructuredCellValue(value)) {
        const { observedValue, expectedValue, hasExpectedValue } = value;
        const observedDisplay = formatObservedValue(observedValue);
        const expectedDisplay = formatExpectedValue(expectedValue);

        if (hasExpectedValue && expectedDisplay) {
            return `${observedDisplay} ${expectedDisplay}`;
        }
        return observedDisplay;
    }

    return String(value);
};

// Helper to render cell value with violation highlighting
const renderCellValue = (value: string | number | null | ValidatePlanCellValue): React.ReactNode => {
    // Handle null or dash values
    if (value === null || value === '-') {
        return <span style={{ color: '#9ca3af' }}>-</span>;
    }

    const tooltipText = getTooltipText(value);

    // Handle structured cell values (from parser)
    if (isStructuredCellValue(value)) {
        const { observedValue, expectedValue, hasExpectedValue } = value;
        const observedDisplay = formatObservedValue(observedValue);
        const expectedDisplay = formatExpectedValue(expectedValue);

        if (hasExpectedValue && expectedDisplay) {
            return (
                <Tooltip
                    title={tooltipText}
                    placement="top"
                    color="white"
                    overlayStyle={{ borderRadius: 4 }}
                    overlayInnerStyle={{ color: colorConstants.darkGrey }}
                >
                    <span>
                        <span style={{ color: '#ef4444', fontWeight: 500 }}>{observedDisplay}</span>
                        <span style={{ color: '#9ca3af', marginLeft: 4 }}>{expectedDisplay}</span>
                    </span>
                </Tooltip>
            );
        }

        return (
            <Tooltip
                title={tooltipText}
                placement="top"
                color="white"
                overlayStyle={{ borderRadius: 4 }}
                overlayInnerStyle={{ color: colorConstants.darkGrey }}
            >
                <span>{observedDisplay}</span>
            </Tooltip>
        );
    }

    // Handle plain string/number values
    return (
        <Tooltip
            title={tooltipText}
            placement="top"
            color="white"
            overlayStyle={{ borderRadius: 4 }}
            overlayInnerStyle={{ color: colorConstants.darkGrey }}
        >
            <span>{String(value)}</span>
        </Tooltip>
    );
};

const SectionTable = ({ table }: { table: ValidatePlanTable }) => {
    const [sortState, setSortState] = useState<SortState>({ columnKey: null, order: null });

    const columns: ColumnsType<ValidatePlanRow> = useMemo(
        () =>
            table.columns.map((col: ValidatePlanColumn, index: number) => ({
                title: col.label,
                dataIndex: col.key,
                key: col.key,
                sorter: true,
                sortOrder: sortState.columnKey === col.key ? sortState.order : null,
                render: (value: string | number | null) => renderCellValue(value),
                ellipsis: { showTitle: false },
                width: index === 0 ? 180 : 140, // First column wider, others have min width
            })),
        [table.columns, sortState],
    );

    // Calculate scroll width based on number of columns
    const scrollX = useMemo(() => {
        const firstColWidth = 180;
        const otherColsWidth = (table.columns.length - 1) * 120;
        return firstColWidth + otherColsWidth;
    }, [table.columns.length]);

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        if (sorter.columnKey) {
            setSortState({
                columnKey: sorter.columnKey,
                order: sorter.order || null,
            });
        } else {
            setSortState({ columnKey: null, order: null });
        }
    };

    const sortedData = useMemo(() => {
        if (!sortState.columnKey || !sortState.order) {
            return table.rows;
        }

        const sorted = [...table.rows].sort((a, b) => {
            const aVal = String(a[sortState.columnKey!] ?? '');
            const bVal = String(b[sortState.columnKey!] ?? '');

            return sortState.order === 'ascend' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        });

        return sorted;
    }, [table.rows, sortState]);

    return (
        <div className="mb-4 last:mb-0">
            <StyledValidatePlanTable
                columns={columns as ColumnsType<any>}
                dataSource={sortedData.map((row, idx) => ({ ...row, key: idx }))}
                pagination={false}
                size="small"
                onChange={handleTableChange}
                scroll={{ x: scrollX, y: 300 }}
                showSorterTooltip={false}
            />
        </div>
    );
};

const PanelHeader = ({ title, severity }: { title: string; severity?: string }) => (
    <div className="flex items-center gap-2">
        {severity === 'error' && <ExclamationCircleFilled style={{ color: colorConstants.red, fontSize: 14 }} />}
        <span className="font-semibold">{title}</span>
    </div>
);

// Component to display metadata fields
const MetadataDisplay = ({ metadata }: { metadata?: ValidatePlanSectionMetadata }) => {
    const metadataEntries = Object.entries(metadata ?? {}).filter(([_, value]) => value !== undefined);

    if (metadataEntries.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-3 text-sm">
            {metadataEntries.map(([key, value]) => (
                <div key={key} className="flex items-center gap-1">
                    <span className="text-gray-600">{key}:</span>
                    <span className="font-semibold text-gray-800">{String(value)}</span>
                </div>
            ))}
        </div>
    );
};

const ValidatePlanDrawer = ({
    open,
    onClose,
    title = 'Validate plan against library',
    sections,
    width = 550,
    topOffset = 0,
    loading = false,
    error = null,
}: ValidatePlanDrawerProps) => {
    // Sections with errors should be expanded by default
    const defaultActiveKeys = useMemo(() => {
        const errorSections = sections?.filter((section) => section.severity === 'error')?.map((section) => section.id);
        if (errorSections && errorSections.length > 0) {
            return errorSections;
        }
        // If no error sections, expand the first section
        return sections?.length > 0 ? [sections[0].id] : [];
    }, [sections]);

    const loadingIcon = <LoadingOutlined style={{ fontSize: 32, color: colorConstants.violet }} spin />;

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center w-full">
                    <span className="font-semibold text-base">{title}</span>
                    <CloseOutlined onClick={onClose} className="cursor-pointer text-gray-500 hover:text-gray-700" />
                </div>
            }
            placement="right"
            onClose={onClose}
            open={open}
            width={width}
            closable={false}
            bodyStyle={{ padding: '16px' }}
            headerStyle={{ borderBottom: 'none', padding: '16px 24px' }}
            contentWrapperStyle={{ top: topOffset, height: `calc(100% - ${topOffset}px)` }}
            maskStyle={{ top: topOffset }}
        >
            {loading ? (
                <div className="flex items-center justify-center h-full">
                    <Spin indicator={loadingIcon} />
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <span className="text-lg font-semibold text-gray-800 mb-2">Something went wrong</span>
                    <span className="text-sm text-gray-500">Unable to validate the plan. Please try again later.</span>
                </div>
            ) : (
                <Collapse
                    defaultActiveKey={defaultActiveKeys}
                    expandIconPosition="end"
                    bordered={false}
                    style={{ background: 'transparent' }}
                >
                    {sections?.map((section) => (
                        <Panel
                            key={section.id}
                            header={<PanelHeader title={section.title} severity={section.severity} />}
                            style={{
                                marginBottom: 12,
                                background: '#f5f5f5',
                                borderRadius: 8,
                                border: 'none',
                            }}
                        >
                            <div className="rounded-md p-3">
                                <MetadataDisplay metadata={section.metadata} />
                                {section.tables.map((table) => (
                                    <SectionTable key={table.id} table={table} />
                                ))}
                            </div>
                        </Panel>
                    ))}
                </Collapse>
            )}
        </Drawer>
    );
};

export default ValidatePlanDrawer;
